import { useId, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { useNavLayer, useNavStack } from '@/hooks/useNavLayer';
import styles from './PrivateBadge.module.css';

interface PrivateBadgeProps {
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Reverent gold pill shown on a prayer card when `isPrivate === true`.
 * The whole pill is tappable; tapping opens a popover with the functional
 * explanation. The popover is where mechanics are explained — never load
 * mechanical text into the badge itself.
 */
export default function PrivateBadge({ size = 'md', className }: PrivateBadgeProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const instanceId = useId();
  const titleId = `private-popover-title-${instanceId}`;
  // Only one modal can be opened through the blocking backdrop, so the shared
  // static surface key follows the NavStack contract even though many badge
  // triggers can be mounted in a virtualized list.
  const layerKey = 'private-badge';
  const nav = useNavStack();
  const zIndex = useNavLayer(layerKey, open, () => setOpen(false));
  const isTop = useSyncExternalStore(nav.subscribe, () => nav.isTopLayer(layerKey));
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const requestClose = () => {
    if (nav.has(layerKey)) nav.requestClose(layerKey);
    else setOpen(false);
  };
  useDialogFocus({
    open,
    active: isTop,
    containerRef: dialogRef,
    initialFocusRef: closeRef,
    onEscape: requestClose,
  });

  return (
    <>
      <button
        type="button"
        className={[
          styles.badge,
          size === 'sm' ? styles.badgeSm : '',
          className ?? '',
        ].filter(Boolean).join(' ')}
        onClick={(e) => {
          // Prevent parent card onClick (e.g., opening prayer detail) from firing.
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={t('private.badgeAria')}
      >
        <span className={styles.star} aria-hidden="true">✦</span>
        <span>{t('private.badge')}</span>
      </button>

      {open && createPortal(
        // Portal to body so the popover escapes any ancestor stacking
        // context (e.g. Garden node cards with `transform`, PrayPage's
        // compose-area `.privateToggleWrap` which uses `translateX(-50%)`
        // and would otherwise trap the backdrop at a lower z-index than
        // the Save button).
        <div
          className={styles.backdrop}
          style={{ zIndex }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onClick={(e) => {
            e.stopPropagation();
            requestClose();
          }}
        >
          <div ref={dialogRef} className={styles.popover} onClick={(e) => e.stopPropagation()} tabIndex={-1}>
            <h2 id={titleId} className={styles.popoverTitle}>
              <span className={styles.popoverStar} aria-hidden="true">✦</span>
              {t('private.popoverTitle')}
            </h2>
            <p className={styles.popoverText}>{t('private.popoverText')}</p>
            <button
              type="button"
              ref={closeRef}
              className={styles.popoverClose}
              onClick={requestClose}
            >
              {t('private.popoverClose')}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
